<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { t } = useI18n();

const {
  filteredCurrentChatAttachments,
  attachments,
  contentType,
  contentAttributes,
} = useMessageContext();

const attachment = computed(() => {
  const att = attachments.value?.[0] || {};
  const url =
    att.dataUrl ||
    att.data_url ||
    att.fileUrl ||
    att.file_url ||
    att.thumbUrl ||
    att.thumb_url ||
    '';
  return {
    ...att,
    dataUrl: url,
  };
});

const isSticker = computed(() => {
  const att = attachment.value || {};
  const url = (att.dataUrl || att.data_url || '').toLowerCase();
  const ext = (att.extension || att.file_type || att.fileType || '')
    .toString()
    .toLowerCase();
  const filename = (att.title || att.filename || '').toString().toLowerCase();
  const mime = (att.contentType || att.content_type || '').toString().toLowerCase();
  const isMsgSticker =
    contentType?.value === 'sticker' ||
    contentAttributes?.value?.is_sticker === true ||
    contentAttributes?.value?.isSticker === true;

  return (
    isMsgSticker ||
    ext === 'webp' ||
    mime === 'image/webp' ||
    mime.includes('webp') ||
    url.includes('.webp') ||
    url.includes('sticker') ||
    filename.includes('sticker') ||
    filename.includes('.webp')
  );
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const showGallery = ref(false);
const isDownloading = ref(false);

const isImageReady = computed(() => {
  if (isSticker.value && attachment.value?.dataUrl) return true;
  return isLoaded.value || Boolean(attachment.value?.dataUrl);
});

onMounted(() => {
  if (attachment.value?.dataUrl && !isSticker.value) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const handleImageError = () => {
  hasError.value = true;
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden"
    :class="[
      isSticker
        ? 'bubble-is-sticker !bg-transparent !p-0 !border-none !shadow-none'
        : 'p-3',
    ]"
    data-bubble-name="image"
    @click="showGallery = true"
  >
    <div
      v-if="hasError && !attachment?.dataUrl"
      class="flex items-center gap-1 text-center rounded-lg p-2"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div
      v-else-if="isImageReady"
      class="relative group rounded-lg overflow-hidden flex items-center justify-center"
      :class="isSticker ? 'w-32 h-32 max-w-[140px] max-h-[140px] p-1' : ''"
    >
      <img
        class="skip-context-menu"
        :class="
          isSticker
            ? 'w-full h-full object-contain drop-shadow-md cursor-pointer select-none transition-transform hover:scale-105'
            : 'max-h-72 object-cover rounded-lg'
        "
        :src="attachment.dataUrl"
        :width="attachment.width"
        :height="attachment.height"
        loading="lazy"
        @error="handleImageError"
      />
      <div
        v-if="!isSticker"
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div
        v-if="!isSticker"
        class="absolute right-2 bottom-2 hidden group-hover:flex gap-2"
      >
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
